import { useEffect } from "react";

import { produce } from "immer";
import { UseFormReturn } from "react-hook-form";

import type { AnyStepFormData } from "../../Steps/AnyStepFormData";
import { useLiveFormEditorContext } from "../LiveFormEditorContext";

import { usePreviewFormWatch } from "./usePreviewFormWatch";

import { useBuildInitialLiveDonationData } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useBuildInitialLiveDonationData";
import { LiveDonationData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { toDonationFormPreview } from "@/common/helpers/formPreview";

type MutateLiveDonationData<T extends AnyStepFormData> = (formData: T, draft: LiveDonationData) => void;

/**
 * This hook keeps the "liveTicketingData" in sync with form updates.
 * It uses "immer" to create immutable updates, ensuring we always have a fresh, updated state.
 */
export const useUpdateDonationDataInPreview = <T extends AnyStepFormData>({
  mutate: mutateLiveDonationData,
  form,
}: {
  mutate: MutateLiveDonationData<T>;
  form: UseFormReturn<T>;
}) => {
  const { setLiveFormData, liveFormData, apiFormObject, isStepChanging } = useLiveFormEditorContext<"donation-form">();
  const { organization } = useCurrentUserContext();
  const { buildInitialLiveDonationData } = useBuildInitialLiveDonationData();

  // #1: Initialize "liveFormData" on step mount
  useEffect(() => {
    if (isStepChanging) {
      return;
    }

    const donationFormPreview = toDonationFormPreview(apiFormObject, organization);
    if (!donationFormPreview) {
      return;
    }

    // Build the initial "liveFormData" object from the API data
    const initialResult = buildInitialLiveDonationData(donationFormPreview);

    // Update the context with the default form data (e.g: placeholder values)
    updateLiveDonationDataWithFormData(initialResult);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepChanging]);

  const syncPreviewWithLatestData = () => {
    if (!liveFormData || isStepChanging) {
      return;
    }

    updateLiveDonationDataWithFormData(liveFormData as LiveDonationData);
  };

  usePreviewFormWatch(form, syncPreviewWithLatestData);

  // Helper function to update the liveDonationData with the form data
  const updateLiveDonationDataWithFormData = (liveDonationDataToPreserve: LiveDonationData) => {
    // React setState does not allow us to mutate the existing object directly.
    // Because liveDonationData is a complex and deeply nested object (e.g: liveDonationData.donation.donationFormFields[0].title),
    // we are using immer.produce to manipulate it as if it were mutable,
    // but in the end immer produces a brand-new, immutable object for us.
    const newLiveData = produce(liveDonationDataToPreserve, (liveDonationDataToMutate) => {
      mutateLiveDonationData(form.getValues(), liveDonationDataToMutate);
    });

    setLiveFormData(newLiveData);
  };
};
